import { Link, useParams } from "react-router-dom";
import { useAgentContext } from "../../context/agent";
import { Alert, Button, Card, Form, Spinner, Row, Col, Toast } from "react-bootstrap";
import { FormEvent, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { addWhatsAppCrendentials } from "../../api/agent";
import { FaWhatsapp, FaEdit, FaTimes, FaCopy, FaCheckCircle, FaExclamationTriangle, FaExternalLinkAlt, FaKey, FaCode } from 'react-icons/fa';

function APIntegration() {
    const [accessToken, setAccessToken] = useState("");
    const [phoneNumberId, setPhoneNumberId] = useState("");
    const [showToast, setShowToast] = useState(false);
    const [errorAlert, setErrorAlert] = useState("");
    const [disabled, setDisabled] = useState(true);
    const [isVerifying, setIsVerifying] = useState(false);

    const { agentId } = useParams();
    const { getAgentById } = useAgentContext();
    const agent = getAgentById(agentId || "");

    const apiURL = process.env.REACT_APP_API_URL;
    const webhookURL = `${apiURL}whatsapp/webhook/${agent?.accessKey}`;

    const queryClient = useQueryClient();
    const { mutateAsync: addWhatsAppAccount, isLoading } = useMutation(addWhatsAppCrendentials, {
        onSuccess: () => {
            queryClient.invalidateQueries(['Agents', agent?.organizationId]);
        },
        onError: (error) => {
            setErrorAlert("Unexpected error. Please sign out and try again.");
        }
    })

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!agent || !agent.accessKey) {
            setErrorAlert("Unexpected error. Please sign out and try again.");
            return
        }
        setErrorAlert("");
        addWhatsAppAccount({
            apiKey: agent.accessKey,
            accessToken,
            phoneNumberId
        })
    }

    useEffect(() => {
        if (agent) {
            setAccessToken(agent.whatsAppAcessToken || "");
            setPhoneNumberId(agent.whatsAppPhoneId || "");
            setDisabled(agent.whatsAppAcessToken !== undefined && agent.whatsAppPhoneId !== undefined);
            if (agent.whatsAppWebhookVerified) setIsVerifying(false);
        }
    }, [agent]);

    useEffect(() => {
        if (!isVerifying) return;

        const timer = setTimeout(() => {
            queryClient.invalidateQueries(['Agents', agent?.organizationId]);
            setIsVerifying(false);
        }, 5000);

        return () => clearTimeout(timer);
    }, [isVerifying]);


    const chatbotURL = `${apiURL}chatagents/${agent?.accessKey}`;

    return (
        <Row>
            <Col>
            {/* Toast for copy confirmation */}
            <Toast
                onClose={() => setShowToast(false)}
                show={showToast}
                delay={2000}
                autohide
                style={{
                    position: 'fixed',
                    // top: '20px',
                    bottom: '20px',
                    right: '20px',
                    zIndex: 1000,
                    backgroundColor: '#2d2d2d',
                    borderColor: '#007bff'
                }}>
                <Toast.Body style={{ color: '#007bff' }}>
                    Copied to clipboard!
                </Toast.Body>
            </Toast>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Header */}
                    <div className="d-flex align-items-center mb-3">
                        <FaCode size={28} style={{ color: '#007bff', marginRight: '12px' }} />
                        <h4 style={{ color: '#fff', margin: 0 }}>API Integration</h4>
                    </div>

                    {/* Info Alert */}
                    <Alert 
                        style={{ 
                            backgroundColor: '#1b2d1b', 
                            borderColor: '#007bff',
                            color: '#b8daff',
                            marginBottom: '24px'
                        }}
                    >
                        For a step-by-step guide on how to setup and use the QuickyChat agent API,
                        please visit our <Link 
                            to="/api-access" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', textDecoration: 'underline' }}
                        >
                            QuickyChat API Integration
                        </Link>.
                    </Alert>
                     <Card style={{ backgroundColor: '#2d2d2d', borderColor: '#444' }}>
                        <Card.Header style={{ backgroundColor: '#333', borderBottom: '1px solid #444', padding: '16px' }}>
                            <div className="d-flex align-items-center">
                                <FaKey size={20} style={{ color: '#fff', marginRight: '12px' }} />
                                <h5 style={{ color: '#fff', margin: 0 }}>API Access Key</h5>
                            </div>
                        </Card.Header>
                        <Card.Body style={{ padding: '24px' }}>
                            <p style={{ color: '#ccc', marginBottom: '20px' }}>
                                Use the following API access key to authenticate your requests to the QuickyChat agent API.
                            </p>
            
                            {/* Preview URL */}
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ 
                                    backgroundColor: '#1a1a1a', 
                                    padding: '12px', 
                                    borderRadius: '8px', 
                                    border: '1px solid #555',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <p className="text-light">{agent?.accessKey}</p>

                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            navigator.clipboard.writeText(agent?.accessKey || '');
                                            setShowToast(true);
                                        }}
                                        style={{
                                            backgroundColor: 'transparent',
                                            borderColor: '#555',
                                            color: '#fff',
                                            marginLeft: '12px',
                                            padding: '4px 8px'
                                        }}
                                    >
                                        <FaCopy size={12} />
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                    <Card style={{ backgroundColor: '#2d2d2d', borderColor: '#444' }}>
                        <Card.Header style={{ backgroundColor: '#333', borderBottom: '1px solid #444', padding: '16px' }}>
                            <div className="d-flex align-items-center">
                                <FaExternalLinkAlt size={20} style={{ color: '#fff', marginRight: '12px' }} />
                                <h5 style={{ color: '#fff', margin: 0 }}>API Endpoint</h5>
                            </div>
                        </Card.Header>
                        <Card.Body style={{ padding: '24px' }}>
                            <p style={{ color: '#ccc', marginBottom: '20px' }}>
                                Use the following API endpoint to interact with your QuickyChat agent programmatically.
                            </p>
            
                            {/* Preview URL */}
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ 
                                    backgroundColor: '#1a1a1a', 
                                    padding: '12px', 
                                    borderRadius: '8px', 
                                    border: '1px solid #555',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <p className="text-light">{chatbotURL}</p>

                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            navigator.clipboard.writeText(chatbotURL);
                                            setShowToast(true);
                                        }}
                                        style={{
                                            backgroundColor: 'transparent',
                                            borderColor: '#555',
                                            color: '#fff',
                                            marginLeft: '12px',
                                            padding: '4px 8px'
                                        }}
                                    >
                                        <FaCopy size={12} />
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </Col>
        </Row>
    )
}

export default APIntegration;